import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { get, debounce } from 'lodash';
import FormikTextField from '../../../../../components/Fields/TextField';
import EditModeCard, { EditModeCardSection } from '../../../../../components/EditModeCard';
import { TP, VISIBILITY } from '../../../../../constants/consts';
import TextButton from '../../../../../components/TextButton';
import { colors } from '../../../../../constants/styleConstants';
import theme from '../../../../../src/theme';
import {
  createEntity,
  deleteEntity,
  getLanguagesAction,
  getList,
  getWholeList,
  updateOne,
} from '../../../../App/actions';
import {
  COMPANIES,
  FESTIVALS,
  LANGUAGES_LIST,
  FESTIVAL_DESCRIPTIONS,
} from '../../../../../constants/endpointConstants';
import UploadImageModal from '../../../../../components/EditComponents/UploadImageModal';
import { getImageUrl, getOption, getUnusedLangsOptions } from '../../../../../utils/common';
import TitleBlock from '../../../../../components/EditComponents/TitleBlock';
import ActionStatusModal from '../../../../../components/ActionStatusModal';
import Button from '../../../../../components/Button';
import { ImageCard } from '../../../../../components/EditComponents/ImageCard';
import { CompanyAbout } from '../../../../../components/EditComponents/CompanyAbout';
import {
  selectCompanies,
  selectFestival,
  selectLoadingFieldName,
  selectFestivalDescriptions,
} from '../../../selectors';
import FormikAutocompleteSelect from '../../../../../components/Fields/FormikAutocompleteSelect';
import {
  getCompaniesAction,
  updateFestivalAction,
  getFestivalDescriptionsAction,
  createFestivalDescriptionAction,
  deleteFestivalDescriptionAction,
  updateFestivalDescriptionAction,
} from '../../../actions';
import AddAnotherField from '../../../../../components/AddAnotherField';
import { selectLanguages } from '../../../../App/selectors';
import { basicInfoStyles } from '../../../../Company/edit/EditCompany/components/CompanyBasicInfo';
import EditFormFooter from '../../../../../components/EditComponents/EditFormFooter';
import NoOptionsBox from '../../../../../components/NoOptionsBox';
import AddNewCompanyModal from '../../../../../components/AddNewCompanyModal';

const useStyles = makeStyles({
  ...basicInfoStyles,
  nameField: {
    display: 'block',
    maxWidth: 343,
    marginBottom: theme.spacing(2),
  },
});

export const FestivalBasicInfo = ({ festival, goToNextTab, languages, descriptions, companies, loadingFieldName }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();

  const festivalCompanies = festival.companies?.map(el => getOption(el));

  const [linkedCompanies, setLinkedCompanies] = React.useState(festivalCompanies?.length ? festivalCompanies : ['']);
  const [newCompanyName, setNewCompanyName] = React.useState(null);
  const [isCompanyModalOpened, setCompanyModalOpened] = React.useState(false);
  const [companyStartSearching, setCompanySearching] = React.useState(false);

  React.useEffect(() => {
    if (languages?.length === 0) {
      dispatch(
        getWholeList({
          endpoint: LANGUAGES_LIST,
          sagaRoutine: getLanguagesAction,
        }),
      );
    }
    dispatch(
      getWholeList({
        endpoint: FESTIVAL_DESCRIPTIONS(festival.id),
        sagaRoutine: getFestivalDescriptionsAction,
        notifyOnError: true,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setCompanySearching(false);
  }, [companies]);

  const [modalImageUrl, setModalImageUrl] = React.useState(null);
  const [idToDelete, setIdToDelete] = React.useState(null);

  const getUnusedLangs = React.useCallback(() => getUnusedLangsOptions(descriptions.data, languages), [
    languages,
    descriptions,
  ]);

  const submitLinkedCompanies = () => {
    if (JSON.stringify(festivalCompanies) === JSON.stringify(linkedCompanies)) {
      goToNextTab();
    } else {
      dispatch(
        updateOne({
          endpoint: FESTIVALS,
          id: festival.id,
          sagaRoutine: updateFestivalAction,
          params: { companies: linkedCompanies.flatMap(el => (el?.value ? [{ id: el.value }] : [])) },
          callback: goToNextTab,
          fieldName: 'linked_companies',
        }),
      );
    }
  };

  const submitLogo = fileData => {
    dispatch(
      updateOne({
        endpoint: FESTIVALS,
        sagaRoutine: updateFestivalAction,
        id: festival.id,
        params: { logo: { file: `@${fileData.filename}` } },
        files: { [fileData.filename]: fileData.file },
        fieldName: 'logo',
      }),
    );
    setModalImageUrl(null);
  };

  const addDescription = (data, callback) => {
    dispatch(
      createEntity({
        endpoint: FESTIVAL_DESCRIPTIONS(festival.id),
        sagaRoutine: createFestivalDescriptionAction,
        params: {
          description: get(data, 'content'),
          language: { id: get(data, 'language.id') },
        },
        callback,
      }),
    );
  };

  const updateDescription = (data, callback) => {
    dispatch(
      updateOne({
        endpoint: FESTIVAL_DESCRIPTIONS(festival.id),
        id: data.id,
        sagaRoutine: updateFestivalDescriptionAction,
        params: {
          description: get(data, 'content'),
          language: { id: get(data, 'language.id') },
        },
        callback,
      }),
    );
  };

  const deleteDescription = () => {
    dispatch(
      deleteEntity({
        endpoint: FESTIVAL_DESCRIPTIONS(festival.id),
        id: idToDelete,
        sagaRoutine: deleteFestivalDescriptionAction,
        callback: () => setIdToDelete(null),
      }),
    );
  };

  const getAssociatedCompanies = debounce((fieldName, query) => {
    dispatch(
      getList({
        endpoint: COMPANIES,
        sagaRoutine: getCompaniesAction,
        fieldName,
        params: {
          queryParams: {
            query,
            show_invalid: true,
            visibility: VISIBILITY.ALL,
          },
        },
      }),
    );
  }, 300);

  const onCompanySelect = (val, index) => {
    setNewCompanyName(null);
    setLinkedCompanies(prevState => Object.assign([], prevState, { [index]: val }));
  };

  const getUnusedCompanies = () => {
    const usedCompaniesIds = linkedCompanies.map(c => c?.value);
    return companies.filter(el => !usedCompaniesIds.includes(el.value));
  };

  const removeCompany = index => {
    setLinkedCompanies(prevState => prevState.filter((el, i) => i !== index));
  };

  const closeAddNewCompanyModal = createdCompany => {
    if (createdCompany?.id) {
      linkedCompanies[newCompanyName?.index] = getOption(createdCompany);
    }
    setCompanyModalOpened(false);
    setNewCompanyName(null);
  };

  return (
    <>
      <EditModeCard className={classes.inputBlock}>
        <EditModeCardSection first>
          <FormikTextField
            className={classes.nameField}
            label={t(`${TP}.FN_FESTIVAL_NAME`)}
            defaultValue={festival?.name}
            disabled
          />
          {linkedCompanies.map((c, index) => (
            <AddAnotherField
              removeFunc={() => removeCompany(index)}
              addFunc={() => onCompanySelect('', index + 1)}
              isLast={index === linkedCompanies.length - 1}
              addButtonPosition="right"
              key={c?.value || `c_${index}`}
              addDisabled={linkedCompanies.some(el => !el)}
              withDelete={linkedCompanies.length > 1}
            >
              <FormikAutocompleteSelect
                label={index ? '' : t(`${TP}.FN_ASSOCIATED_WITH_COMPANY`)}
                instanceId={`associatedCompany.${index}`}
                t={t}
                options={getUnusedCompanies()}
                minSearchCharsLength={3}
                onInputTextChange={v => {
                  setCompanySearching(true);
                  setNewCompanyName({ index, name: v });
                  getAssociatedCompanies(`associatedCompany.${index}`, v);
                }}
                onFocus={() => {
                  setCompanySearching(true);
                  getAssociatedCompanies(`associatedCompany.${index}`, '');
                }}
                onChange={val => onCompanySelect(val, index)}
                field={{ name: `associatedCompany.${index}`, value: c }}
                isLoading={loadingFieldName === `associatedCompany.${index}`}
                startAggregations={companyStartSearching}
                customComponents={{
                  NoOptionsMessage: () => (
                    <NoOptionsBox
                      buttonText={`${TP}.FN_ADD_NEW_COMPANY`}
                      openFormFunc={() => setCompanyModalOpened(true)}
                      t={t}
                    />
                  ),
                }}
                staticOption={
                  <Button onClick={() => setCompanyModalOpened(true)}>{t(`${TP}.FN_ADD_NEW_COMPANY`)}</Button>
                }
                isClearable
              />
            </AddAnotherField>
          ))}
        </EditModeCardSection>
      </EditModeCard>
      <div className={classes.imageBlock}>
        <TitleBlock title={`${TP}.FN_FESTIVAL_LOGO_TITLE`} subtitle={`${TP}.FN_FESTIVAL_LOGO_SUBTITLE`} />
        <Divider />
        {festival?.logo ? (
          <ImageCard
            image={festival.logo}
            editFunc={imageUrl => setModalImageUrl(getImageUrl(imageUrl))}
            loading={loadingFieldName === 'logo'}
            contentClassName={classes.logoCard}
          />
        ) : (
          <div className={classes.imagesButtonBlock}>
            <span className={classes.noImages}>{t(`${TP}.FN_NO_IMAGES_ADDED`)}</span>
            <TextButton
              className={classes.addImageButton}
              endIcon={<AddIcon style={{ color: colors.blueLight }} />}
              onClick={() => setModalImageUrl(true)}
            >
              {t(`${TP}.FN_IMAGES_TITLE`)}
            </TextButton>
          </div>
        )}
      </div>
      <CompanyAbout
        descriptions={descriptions}
        languages={getUnusedLangs()}
        addDescription={addDescription}
        updateDescription={updateDescription}
        deleteDescription={setIdToDelete}
      />
      <EditFormFooter
        submitFunc={submitLinkedCompanies}
        lastUpdatedDate={festival?.updatedAt}
        isLoading={loadingFieldName === 'linked_companies'}
      />
      {modalImageUrl && (
        <UploadImageModal
          onClose={() => {
            setModalImageUrl(null);
          }}
          imageUrl={modalImageUrl}
          submitImage={submitLogo}
          title={`${TP}.FN_ADD_LOGO`}
        />
      )}
      {Boolean(idToDelete) && (
        <ActionStatusModal
          isOpened
          onClose={() => setIdToDelete(null)}
          type="warning"
          title={t(`${TP}.FN_DELETE_DESCRIPTION_CONFIRMATION`)}
          buttons={[
            <TextButton onClick={() => setIdToDelete(null)} key="approve-button">
              {t(`${TP}.m_CANCEL`)}
            </TextButton>,
            <Button onClick={deleteDescription} key="cancel-button" isLoading={descriptions.loading}>
              {t(`${TP}.m_DELETE`)}
            </Button>,
          ]}
        />
      )}
      {isCompanyModalOpened && (
        <AddNewCompanyModal onClose={closeAddNewCompanyModal} companyName={newCompanyName.name} />
      )}
    </>
  );
};

FestivalBasicInfo.propTypes = {
  festival: PropTypes.object.isRequired,
  goToNextTab: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  festival: selectFestival(),
  languages: selectLanguages(),
  descriptions: selectFestivalDescriptions(),
  companies: selectCompanies(),
  loadingFieldName: selectLoadingFieldName(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(FestivalBasicInfo);
